import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Interest cost + credit-margin calculator.
 *
 * Rebuild of the operator's Excel model (Sales/Purchase on credit) with three
 * discount tiers. Purely frontend — no server calls, no persistence, so it is
 * safe to iterate on ahead of the demo.
 */

function fmtINR(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
    style: "currency",
    currency: "INR",
  }).format(n);
}

function calc({ dailyLitres, days, price, margin, interestRate, discount = 0 }) {
  const litres = dailyLitres * days;
  const sales = litres * price;
  const netMargin = litres * (margin - discount);
  // Interest on the outstanding for `days` days, expressed as an annualised drag.
  // interestValueForPeriod = sales * rate * (days / 365)
  const interestForPeriod = (sales * interestRate * days) / 365;
  const interestYearly = interestForPeriod * (365 / days);
  const netAfterInterest = netMargin - interestForPeriod;
  return {
    litres,
    sales,
    netMargin,
    interestForPeriod,
    interestYearly,
    netAfterInterest,
  };
}

// Discount tiers mirror the operator's original Excel model — module-level
// so React sees a stable reference across renders (no useMemo needed).
const DISCOUNT_TIERS = [0, 1, 3, 7];

export default function InterestCalc() {
  const [dailyLitres, setDailyLitres] = useState(1000);
  const [salesDays, setSalesDays] = useState(7);
  const [purchaseDays, setPurchaseDays] = useState(10);
  const [price, setPrice] = useState(90.23);
  const [margin, setMargin] = useState(2.57);
  const [interestRate, setInterestRate] = useState(0.089); // 8.9% IOCL

  const salesRows = useMemo(
    () =>
      DISCOUNT_TIERS.map((d) => ({
        discount: d,
        ...calc({ dailyLitres, days: salesDays, price, margin, interestRate, discount: d }),
      })),
    [dailyLitres, salesDays, price, margin, interestRate],
  );
  const purchaseRow = useMemo(
    () => calc({ dailyLitres, days: purchaseDays, price, margin, interestRate, discount: 0 }),
    [dailyLitres, purchaseDays, price, margin, interestRate],
  );

  return (
    <div className="space-y-6" data-testid="interest-calc-root">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Interest Cost &amp; Credit Margin
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Compare net margins at different discount tiers when selling or buying fuel on credit. All
          numbers are illustrative; adjust inputs to match the current IOCL contract.
        </p>
      </div>

      <Card className="p-4 border-slate-300">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label htmlFor="daily">Daily litres</Label>
            <Input
              id="daily"
              data-testid="calc-daily-litres"
              type="number"
              value={dailyLitres}
              onChange={(e) => setDailyLitres(+e.target.value)}
              className="border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="sales-days">Sales credit days</Label>
            <Input
              id="sales-days"
              data-testid="calc-sales-days"
              type="number"
              value={salesDays}
              onChange={(e) => setSalesDays(+e.target.value)}
              className="border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="purchase-days">Purchase credit days</Label>
            <Input
              id="purchase-days"
              data-testid="calc-purchase-days"
              type="number"
              value={purchaseDays}
              onChange={(e) => setPurchaseDays(+e.target.value)}
              className="border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="price">Fuel price ₹/L</Label>
            <Input
              id="price"
              data-testid="calc-price"
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(+e.target.value)}
              className="border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="margin">Margin ₹/L</Label>
            <Input
              id="margin"
              data-testid="calc-margin"
              type="number"
              step="0.01"
              value={margin}
              onChange={(e) => setMargin(+e.target.value)}
              className="border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="rate">Interest rate (annual)</Label>
            <Input
              id="rate"
              data-testid="calc-rate"
              type="number"
              step="0.001"
              value={interestRate}
              onChange={(e) => setInterestRate(+e.target.value)}
              className="border-slate-300"
            />
          </div>
        </div>
      </Card>

      {/* Sales tiers */}
      <Card className="p-4 border-slate-300">
        <h2 className="text-base font-semibold text-slate-900 mb-3">
          Fuel sales on credit — {salesDays}-day cycle
        </h2>
        <div className="overflow-auto">
          <table className="w-full text-sm border border-slate-300">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-2 py-1 border-b border-slate-300">Discount / L</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Litres</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Sales</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Net Margin</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Interest ({salesDays}d)</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Interest / year</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Net after interest</th>
              </tr>
            </thead>
            <tbody>
              {salesRows.map((row) => (
                <tr key={row.discount} className="border-b border-slate-200">
                  <td className="px-2 py-1 font-medium">₹ {row.discount}</td>
                  <td className="px-2 py-1 text-right">{row.litres.toLocaleString("en-IN")}</td>
                  <td className="px-2 py-1 text-right">{fmtINR(row.sales)}</td>
                  <td
                    className={`px-2 py-1 text-right font-medium ${
                      row.netMargin < 0 ? "text-rose-700" : "text-emerald-700"
                    }`}
                  >
                    {fmtINR(row.netMargin)}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-700">
                    {fmtINR(row.interestForPeriod)}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-700">{fmtINR(row.interestYearly)}</td>
                  <td
                    className={`px-2 py-1 text-right font-semibold ${
                      row.netAfterInterest < 0 ? "text-rose-700" : "text-emerald-700"
                    }`}
                  >
                    {fmtINR(row.netAfterInterest)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Discount tiers 0 / 1 / 3 / 7 mirror the operator&rsquo;s original spreadsheet.
          Values in red indicate margin is fully consumed by interest cost.
        </p>
      </Card>

      {/* Purchases block */}
      <Card className="p-4 border-slate-300">
        <h2 className="text-base font-semibold text-slate-900 mb-3">
          Fuel purchases on credit — {purchaseDays}-day cycle
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="p-2 border border-slate-300 rounded-sm">
            <div className="text-xs text-slate-500 uppercase">Litres</div>
            <div className="font-semibold">{purchaseRow.litres.toLocaleString("en-IN")}</div>
          </div>
          <div className="p-2 border border-slate-300 rounded-sm">
            <div className="text-xs text-slate-500 uppercase">Purchase value</div>
            <div className="font-semibold">{fmtINR(purchaseRow.sales)}</div>
          </div>
          <div className="p-2 border border-slate-300 rounded-sm">
            <div className="text-xs text-slate-500 uppercase">Interest ({purchaseDays}d)</div>
            <div className="font-semibold">{fmtINR(purchaseRow.interestForPeriod)}</div>
          </div>
          <div className="p-2 border border-slate-300 rounded-sm">
            <div className="text-xs text-slate-500 uppercase">Effective saving vs pay-upfront</div>
            <div className="font-semibold text-emerald-700">
              {fmtINR(purchaseRow.interestForPeriod)}
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Longer credit from IOCL = larger effective saving; assumes the same
          {" "}{(interestRate * 100).toFixed(1)}% cost of capital.
        </p>
      </Card>
    </div>
  );
}
